import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { statusColors } from '../theme'
import { formatFullDate, getDaySchedule, getStandardHours } from '../utils'
import { allocateSchedule } from 'src/actions/timesheet.action'
import { getTaskdropdownData } from 'src/actions/project-management.action'
import { successSnackBar, warningSnackBar } from 'src/actions/common.action'
import { getUserWorkHourAssignment, getWorkHourSetting } from 'src/actions/work-hour-settings.action'
import { createChainedAllocationTimes, getAssignmentSetting, getAssignmentSettingId } from '../work-hour-allocation'
import { filterLockedSections, convertToReactSelectFormat } from 'src/util/section-dropdown-format'
import { TIMESHEET_TASK_DROPDOWN_PARAMS } from '../../task-dropdown-options'
import CustomInput from 'src/components/inputs/new-input'
import SelectInput from 'src/components/inputs/select'
import MultiSelect from 'src/components/inputs/multi-select'
import ConfirmationPopover from 'src/components/confirmation-popover'
import { roundTimesheetHours, toDecimal } from 'src/util/decimal-utils'
import { formatDecimalTime } from 'src/util/common'

const WH_WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']



/**
 * Calculates average daily standard hours from a work-hour setting's weekday rules.
 *
 * @param {Object} setting - Work hour setting object
 * @returns {number|null} Rounded average daily hours, or null if no rules exist
 */
export function getDailyHoursFromSetting(setting) {
  const rules = setting?.weekday_rules
  if (!rules) return null
  let totalMinutes = 0
  let daysWithRules = 0
  WH_WEEKDAY_NAMES.forEach((day) => {
    const dayRules = rules[day]
    if (!Array.isArray(dayRules) || dayRules.length === 0) return
    let dayMinutes = 0
    let hasStandard = false
    dayRules.forEach((rule) => {
      if (rule.time_type !== 'STANDARD') return
      hasStandard = true
      // Hourly-mode bands carry an Hours value directly, with no start/end to compute from
      if (Number.isFinite(rule.hours)) {
        dayMinutes += rule.hours * 60
        return
      }
      const [sh, sm] = String(rule.start || '').split(':').map(Number)
      const [eh, em] = String(rule.end || '').split(':').map(Number)
      if (!Number.isFinite(sh) || !Number.isFinite(eh)) return
      let startMins = sh * 60 + (sm || 0)
      let endMins = eh * 60 + (em || 0)
      if (endMins <= startMins) endMins += 24 * 60
      dayMinutes += endMins - startMins
    })
    if (!hasStandard) return
    daysWithRules++
    totalMinutes += dayMinutes
  })
  if (daysWithRules === 0) return null
  return Math.round((totalMinutes / daysWithRules / 60) * 10) / 10
}

/**
 * Confirm Allocations Dialog component for batch workforce scheduling.
 *
 * Supports assigning multiple tasks/projects with custom hour allocations
 * chained sequentially across selected staff and day slots.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Modal visibility flag
 * @param {Function} props.onClose - Modal close handler
 * @param {Array} props.staff - List of selected staff objects
 * @param {Map} props.selectionByStaff - Selected cells grouped by staff ID
 * @param {Map} props.scheduleMap - Current scheduling grid state map
 * @param {Map} [props.availability] - Staff availability map
 * @param {Array} props.projects - Available project options
 * @param {Function} props.onConfirm - Callback triggered after successful creation
 * @returns {React.ReactElement} Rendered dialog
 */
export default function ConfirmAllocationsDialog({
  open,
  onClose,
  staff,
  selectionByStaff,
  scheduleMap,
  availability,
  projects,
  onConfirm,
}) {
  const dispatch = useDispatch()
  const globalSetting = useSelector((state) => state.globalSetting)

  const standardHours = getStandardHours(globalSetting?.letterhead)

  const [items, setItems] = useState(() => [
    {
      id: 'alloc-0',
      projectId: '',
      selectedTask: null,
      hours: standardHours || 8,
    }
  ])
  const projectTasksMapRef = useRef({})
  const [loadingProjects, setLoadingProjects] = useState({})
  const [expanded, setExpanded] = useState(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [deleteRowTarget, setDeleteRowTarget] = useState(null)

  const loadTasksForProject = useCallback(async (projId) => {
    if (!projId || projectTasksMapRef.current[projId]) return
    setLoadingProjects((prev) => ({ ...prev, [projId]: true }))
    try {
      const response = await dispatch(getTaskdropdownData(projId, TIMESHEET_TASK_DROPDOWN_PARAMS))
      const filtered = filterLockedSections(response || [], [])
      const formatted = convertToReactSelectFormat(filtered, null, true)
      projectTasksMapRef.current[projId] = formatted
    } catch {
      projectTasksMapRef.current[projId] = []
    } finally {
      setLoadingProjects((prev) => ({ ...prev, [projId]: false }))
    }
  }, [dispatch])

  // Reset state each time dialog opens
  useEffect(() => {
    if (open) {
      setItems([
        {
          id: `alloc-${Date.now()}-0`,
          projectId: '',
          selectedTask: null,
          hours: standardHours || 8,
        }
      ])
      setExpanded(new Set())
      setDone(false)
      setDeleteRowTarget(null)
    } else {
      setDeleteRowTarget(null)
    }
  }, [open, standardHours])

  const totalDailyHours = useMemo(() => {
    return items.reduce((acc, it) => {
      const parsed = parseFloat(it.hours)
      return acc + (Number.isFinite(parsed) && parsed > 0 ? parsed : 0)
    }, 0)
  }, [items])

  const roundedDailyHours = roundTimesheetHours(totalDailyHours)

  const handleAddRow = () => {
    if (roundedDailyHours >= 24) return
    const remaining = Math.max(0, roundTimesheetHours(24 - roundedDailyHours))
    const initialHours = remaining > 0 ? roundTimesheetHours(Math.min(2.0, remaining)) : 2.0
    const prevProjId = items[items.length - 1]?.projectId || ''
    const newItem = {
      id: `alloc-${Date.now()}-${items.length}`,
      projectId: prevProjId,
      selectedTask: null,
      hours: initialHours,
    }
    setItems((prev) => [...prev, newItem])
    if (prevProjId) {
      loadTasksForProject(prevProjId)
    }
  }

  const handleRemoveRow = (id) => {
    if (items.length <= 1) return
    setItems((prev) => prev.filter((it) => it.id !== id))
  }

  const handleProjectChange = (id, newProjectId) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, projectId: newProjectId, selectedTask: null } : it))
    )
    if (newProjectId) {
      loadTasksForProject(newProjectId)
    }
  }

  const handleTaskChange = (id, taskOption) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, selectedTask: taskOption } : it))
    )
  }

  const handleHoursChange = (id, val) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, hours: val } : it))
    )
  }

  const rows = useMemo(() => {
    return staff
      .filter((s) => (selectionByStaff.get(s.id)?.length ?? 0) > 0)
      .map((s) => {
        const keys = [...(selectionByStaff.get(s.id) ?? [])].sort()
        const conflictInstances = keys
          .map((k) => ({ key: k, ...getDaySchedule(scheduleMap, s.id, k, availability, standardHours) }))
          .filter((d) => d.bookedHours > 0 || d.status === 'off')
          .map((d) => ({
            key: d.key,
            type: d.status === 'off' ? 'unavailable' : 'booking',
            status: d.status,
            project: d.project || 'Existing booking',
            bookedHours: d.bookedHours,
            capacityHours: d.capacityHours,
            fullDay: d.status === 'full',
          }))
        const hours = roundTimesheetHours(keys.length * roundedDailyHours)
        return {
          staff: s,
          days: keys.length,
          dailyHours: roundedDailyHours,
          hours,
          cost: hours * (s.hourlyRate || 0), // calculated, display hidden in v1
          conflicts: conflictInstances.length,
          conflictInstances,
        }
      })
  }, [staff, selectionByStaff, scheduleMap, availability, standardHours, roundedDailyHours])

  const totals = useMemo(
    () => ({
      days: rows.reduce((a, r) => a + r.days, 0),
      hours: rows.reduce((a, r) => a + r.hours, 0),
      cost: rows.reduce((a, r) => a + r.cost, 0), // hidden in v1
      conflicts: rows.reduce((a, r) => a + r.conflicts, 0),
    }),
    [rows],
  )

  function toggle(id) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleConfirm() {
    if (items.length === 0) return
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (!it.projectId) {
        dispatch(warningSnackBar(`Please select a project for task row #${i + 1}`))
        return
      }
      const h = parseFloat(it.hours)
      if (!Number.isFinite(h) || h <= 0) {
        dispatch(warningSnackBar(`Please enter valid hours for task row #${i + 1}`))
        return
      }
      it.hours = roundTimesheetHours(it.hours)
    }
    if (roundedDailyHours > 24) {
      dispatch(warningSnackBar(`Total daily hours cannot exceed 24h (currently ${roundedDailyHours}h)`))
      return
    }

    setSubmitting(true)
    try {
      const staffById = new Map(staff.map((s) => [s.id, s]))
      const projectsById = new Map(projects.map((p) => [p.id, p]))

      const users = await Promise.all([...selectionByStaff.entries()].map(async ([staffId, days]) => {
        const s = staffById.get(staffId)
        if (!s || !days.length) return null

        const assignmentResponse = await dispatch(getUserWorkHourAssignment(staffId))
        const assignmentSetting = getAssignmentSetting(assignmentResponse)
        const settingId = getAssignmentSettingId(assignmentResponse)
        const setting = assignmentSetting || (settingId ? await dispatch(getWorkHourSetting(settingId)) : null)
        const resolvedSetting = setting?.data || setting
        if (!resolvedSetting) throw new Error(`${s.name} has no active work-hour setting.`)

        const allocations = []
        days.forEach((day) => {
          let currentLatestEndTime = scheduleMap.get(`${staffId}:${day}`)?.latestEndTime || null
          items.forEach((item) => {
            const itemHours = parseFloat(item.hours) || 0
            const proj = projectsById.get(item.projectId) || { id: item.projectId, type: 'PROJECT' }
            const times = createChainedAllocationTimes(resolvedSetting, day, itemHours, currentLatestEndTime, globalSetting?.letterhead?.start_time)
            if (!times) throw new Error(`${s.name} has no configured work-hour period for ${day}.`)
            currentLatestEndTime = times.endTime

            allocations.push({
              id: '',
              projectId: proj.id,
              projectType: proj.type || 'PROJECT',
              stageId: item.selectedTask?.isStage ? (item.selectedTask.value || '') : '',
              taskId: item.selectedTask && !item.selectedTask.isStage ? (item.selectedTask.value || '') : '',
              taskType: item.selectedTask?.taskType || (item.selectedTask && !item.selectedTask.isStage ? 'TASK' : ''),
              taskName: item.selectedTask?.displayName || item.selectedTask?.label || '',
              startTime: times.startTime,
              endTime: times.endTime,
              allDayWithBreaks: true,
              hourlyRate: s.hourlyRate || 0,
              previousType: '',
            })
          })
        })

        return { id: staffId, leaves: [], allocations }
      }))

      if (users.some((user) => !user)) throw new Error('Unable to resolve selected users for scheduling.')
      await dispatch(allocateSchedule({ users }))
      dispatch(successSnackBar('Allocations created successfully'))
      setDone(true)
      setTimeout(() => {
        onConfirm()
        setDone(false)
      }, 800)
    } catch (e) {
      dispatch(warningSnackBar(e?.message || 'Unable to create allocations from the selected work-hour settings.'))
      console.error('[ConfirmAllocations] submit error:', e)
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = items.length > 0 &&
    items.every((it) => !!it.projectId && parseFloat(it.hours) > 0) &&
    roundedDailyHours <= 24 &&
    !submitting &&
    !done

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="scheduling-dialog" disableScrollLock>
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs>
            <div className="d-flex align-items-center gap-2">
              <span className="dialog-header-title">Confirm Allocations</span>
              <Chip
                size="small"
                color={roundedDailyHours > 24 ? 'error' : 'primary'}
                label={`${roundedDailyHours}h (${formatDecimalTime(roundedDailyHours)}) total`}
                sx={{ fontWeight: 600 }}
              />
            </div>
            <div className="dialog-subtitle">
              {rows.length} {rows.length === 1 ? 'person' : 'people'} · {totals.days} day-slots · {totals.hours}h
            </div>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ color: '#d0021b' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-5">
            <CheckCircleIcon sx={{ fontSize: 48, color: '#2e9e5b' }} />
            <span style={{ fontSize: 16, fontWeight: 500 }}>Allocations created</span>
          </div>
        ) : (
          <>
            {totals.conflicts > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                {totals.conflicts} of the selected days have a conflict — an existing booking, or a day the
                person isn't scheduled to work. You can still confirm; conflicting allocations will be created as selected.
              </Alert>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell align="right">Daily Hrs</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  {/* Cost column hidden in v1 — kept for future display */}
                  <TableCell align="right" style={{ display: 'none' }}>Cost</TableCell>
                  <TableCell align="right">Conflicts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isOpen = expanded.has(r.staff.id)
                  return [
                    <TableRow key={r.staff.id}>
                      <TableCell>
                        <div className="staff-name">{r.staff.name}</div>
                        <div className="staff-meta">{r.staff.role}</div>
                      </TableCell>
                      <TableCell align="right">{r.days}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <span>{r.dailyHours}h</span>
                      </TableCell>
                      <TableCell align="right">{r.hours}</TableCell>
                      {/* Cost cell hidden in v1 */}
                      <TableCell align="right" style={{ display: 'none' }}>${r.cost.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {r.conflicts > 0 ? (
                          <Chip
                            size="small"
                            color="warning"
                            onClick={() => toggle(r.staff.id)}
                            icon={<WarningAmberIcon />}
                            deleteIcon={
                              <KeyboardArrowDownIcon
                                sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                              />
                            }
                            onDelete={() => toggle(r.staff.id)}
                            label={r.conflicts}
                            aria-expanded={isOpen}
                          />
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label="0" />
                        )}
                      </TableCell>
                    </TableRow>,

                    <TableRow key={`${r.staff.id}-detail`}>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <div className="py-2">
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#666' }}>
                              Conflicts ({r.conflicts})
                            </div>
                            <div className="d-flex flex-column gap-2 mt-2">
                              {r.conflictInstances.map((c) => (
                                <div key={c.key} className="conflict-instance d-flex flex-wrap align-items-center justify-content-between gap-2">
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="conflict-dot"
                                      style={{
                                        background: c.type === 'unavailable' ? statusColors.off.dot
                                          : c.status === 'over' ? statusColors.over.dot
                                            : c.fullDay ? statusColors.full.dot
                                              : statusColors.partial.dot,
                                      }}
                                    />
                                    <span style={{ fontWeight: 600 }}>{formatFullDate(c.key)}</span>
                                  </div>
                                  {c.type === 'unavailable' ? (
                                    <Chip size="small" label="Not a working day for this person" />
                                  ) : (
                                    <div className="d-flex align-items-center gap-2" style={{ color: '#666' }}>
                                      <span style={{ color: '#333' }}>{c.project}</span>
                                      <span className="d-flex align-items-center gap-1">
                                        <AccessTimeIcon style={{ fontSize: 14 }} />
                                        {c.bookedHours}h / {c.capacityHours}h
                                      </span>
                                      <Chip
                                        size="small"
                                        color={c.status === 'over' ? 'error' : c.fullDay ? 'primary' : 'warning'}
                                        label={
                                          c.status === 'over'
                                            ? `${roundTimesheetHours(toDecimal(c.bookedHours).minus(toDecimal(c.capacityHours)))}h overbooked`
                                            : c.fullDay
                                              ? 'Booked'
                                              : `${roundTimesheetHours(toDecimal(c.capacityHours).minus(toDecimal(c.bookedHours)))}h free`
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </Collapse>
                      </TableCell>
                    </TableRow>,
                  ]
                })}
              </TableBody>
            </Table>

            {/* Multi-item project + task + hour allocations */}
            <div className="mt-4">
              <div className="task-allocations-title">
                Task Allocations
              </div>
              <div className="allocation-table-container">
                <div className="allocation-table-header">
                  <div className="col-badge">#</div>
                  <div className="col-project">
                    Project <span className="text-danger">*</span>
                  </div>
                  <div className="col-task">Task (optional)</div>
                  <div className="col-hours">Hours</div>
                  <div className="col-duration">Duration</div>
                  <div className="col-action" />
                </div>

                <div className="allocation-table-body">
                  {items.map((item, index) => {
                    const tasks = projectTasksMapRef.current[item.projectId] || []
                    const isLoading = Boolean(loadingProjects[item.projectId])
                    const rawH = item.hours
                    const parsedH = parseFloat(rawH)
                    const isHoursInvalid = rawH === '' || !Number.isFinite(parsedH) || parsedH <= 0 || parsedH > 24
                    const durationFormatted = isHoursInvalid ? '0H' : formatDecimalTime(item.hours)
                    return (
                      <div key={item.id} className="allocation-table-row">
                        <div className="col-badge">
                          <span className="task-row-badge">#{index + 1}</span>
                        </div>

                        <div className="col-project">
                          <SelectInput
                            label=""
                            required={true}
                            placeholder="Select Project"
                            value={item.projectId}
                            onChange={(e) => handleProjectChange(item.id, e.target.value)}
                            items={projects.map((p) => ({ value: p.id, label: p.name }))}
                          />
                        </div>

                        <div className="col-task">
                          <MultiSelect
                            label=""
                            placeholder={isLoading ? 'Loading tasks...' : 'Select Task'}
                            items={tasks}
                            value={item.selectedTask}
                            onChangeHandler={(option) => handleTaskChange(item.id, option)}
                            isStage={true}
                            disabled={!item.projectId || isLoading}
                            isClearable={true}
                          />
                        </div>

                        <div className="col-hours">
                          <CustomInput
                            label=""
                            name={`hours-${item.id}`}
                            type="number"
                            value={item.hours}
                            onChange={(e) => handleHoursChange(item.id, e.target.value)}
                            step="any"
                            min={0.1}
                            max={24}
                            customInputClass={isHoursInvalid ? 'hours-input-invalid' : ''}
                          />
                        </div>

                        <div className="col-duration">
                          <span className="task-row-duration-pill">
                            {durationFormatted}
                          </span>
                        </div>

                        <div className="col-action">
                          {items.length > 1 ? (
                            <IconButton
                              size="small"
                              onClick={(e) => setDeleteRowTarget({ anchorEl: e.currentTarget, rowId: item.id })}
                              aria-label={`Remove allocation row ${index + 1}`}
                              title="Remove allocation"
                              className="row-delete-icon-btn"
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          ) : (
                            <div className="action-placeholder" />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="allocation-table-footer">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  disabled={roundedDailyHours >= 24}
                  className="add-allocation-btn"
                >
                  Add Task / Allocation
                </Button>
                <div className="total-hours-wrapper">
                  <span>Total Daily Hours:</span>
                  <span className={`total-hours-pill ${roundedDailyHours > 24 ? 'exceeded' : ''}`}>
                    {roundedDailyHours}h ({formatDecimalTime(roundedDailyHours)})
                    {roundedDailyHours > 24 && ' (Exceeds 24h limit)'}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <button className="cancel-btn-white" onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className={`btnPrimary ms-2 ${!canSubmit ? 'save-btn-disabled' : ''}`}
          disabled={!canSubmit}
          onClick={handleConfirm}
          type="button"
        >
          {submitting ? 'Saving...' : 'Confirm & Assign'}
        </button>
      </DialogActions>

      {/* Popover confirmation for Task Row Deletion */}
      <ConfirmationPopover
        open={Boolean(deleteRowTarget)}
        anchorEl={deleteRowTarget?.anchorEl}
        onClose={() => setDeleteRowTarget(null)}
        onConfirm={() => {
          if (deleteRowTarget?.rowId) {
            handleRemoveRow(deleteRowTarget.rowId)
          }
          setDeleteRowTarget(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        arrowPosition="right"
        id="popover-delete-allocation-row"
      />
    </Dialog>
  )
}

ConfirmAllocationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  staff: PropTypes.array.isRequired,
  selectionByStaff: PropTypes.instanceOf(Map).isRequired,
  scheduleMap: PropTypes.instanceOf(Map).isRequired,
  availability: PropTypes.instanceOf(Map),
  projects: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
}
